import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AppointmentServices from "./AppointmentServices";
import { employeesByRoleAtLocation } from "../../services/api";
import { useWizardContext } from "./AppointmentWizardContext";
import { role } from "shared/src/roles.mjs";
import { useServiceData } from "./flow";

export default function AppointmentNoteFields() {
  const { t } = useTranslation();

  const [coordinatorOptions, setCoordinatorOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const {
    selectedCoordinators,
    setSelectedCoordinators,
    selectedDoctors,
    setSelectedDoctors,
    floorManagerNotes,
    setFloorManagerNotes,
    nextAppointmentNote,
    setNextAppointmentNote,
    clientData,
    selectedServiceId,
    setSelectedServiceId,
    filledFormsData,
    setFilledFormsData,
    carryOverNextAppointmentNote,
    setCarryOverNextAppointmentNote,
  } = useWizardContext();

  const { service, loading: serviceLoading } =
    useServiceData(selectedServiceId);

  const filledFormDataRef = useRef({});
  const didInitRef = useRef(false);
  const sourceNurseNoteRef = useRef("");

  const locationId =
    clientData?.location_id || localStorage.getItem("currentLocationId");

  const idsOf = (arr) =>
    Array.isArray(arr) ? arr.map((e) => String(e.id)) : [];

  const mapIdsToEmployees = (ids, options) =>
    ids
      .map((id) => options.find((o) => String(o.id) === String(id)))
      .filter(Boolean);

  const fullName = (o) => `${o?.first_name ?? ""} ${o?.last_name ?? ""}`.trim();
  const renderNames = (ids = [], options = []) => {
    if (!ids.length) return t("appointmentNotes.selectOption");
    const byId = new Map(options.map((o) => [String(o.id), o]));
    return ids
      .map((id) => fullName(byId.get(String(id))))
      .filter(Boolean)
      .join(", ");
  };

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;

    (async () => {
      try {
        const [coorRes, docRes] = await Promise.all([
          employeesByRoleAtLocation({
            location_id: locationId,
            roleName: role?.Treatment_Coordinator || "Treatment_Coordinator",
            page: { limit: 200, offset: 0 },
          }),
          employeesByRoleAtLocation({
            location_id: locationId,
            roleName: role?.Doctor || "Doctor",
            page: { limit: 200, offset: 0 },
          }),
        ]);
        if (cancelled) return;
        setCoordinatorOptions(coorRes?.data?.employees?.data ?? []);
        setDoctorOptions(docRes?.data?.employees?.data ?? []);
      } catch (e) {
        console.error("Failed to load staff options:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [locationId]);

  useEffect(() => {
    if (!clientData?.todaysAppointments?.length) return;
    const currentAppointment = clientData.todaysAppointments.find(
      (a) => a.id === clientData.currentAppointmentId,
    );
    if (currentAppointment?.service?.id) {
      setSelectedServiceId(currentAppointment.service.id);
    }

    if (!didInitRef.current) {
      sourceNurseNoteRef.current = nextAppointmentNote || "";
      if (sourceNurseNoteRef.current) {
        setNextAppointmentNote(sourceNurseNoteRef.current);
      } else {
        setCarryOverNextAppointmentNote(false);
      }
      didInitRef.current = true;
    }
  }, [clientData]);

  const handleFormChange = (templateId, formData) => {
    setFilledFormsData((prev) => ({
      ...prev,
      [templateId]: formData,
    }));
    filledFormDataRef.current = {
      ...filledFormDataRef.current,
      [templateId]: formData,
    };
  };

  const handleRadioChange = (value) => {
    setCarryOverNextAppointmentNote(value);
    setNextAppointmentNote(value ? sourceNurseNoteRef.current : "");
  };

  const handleTextChange = (e) => {
    setNextAppointmentNote(e.target.value);
    if (carryOverNextAppointmentNote) setCarryOverNextAppointmentNote(false);
  };

  if (serviceLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 2 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFormSubmitted(true);
        }}
        id="jsonform"
      />
      {service != null && (
        <AppointmentServices
          service={service}
          formSubmitted={formSubmitted}
          onFormChange={handleFormChange}
          initialFormData={filledFormsData}
        />
      )}
      <Typography variant="h2" sx={{ mb: 2 }} fontWeight="normal">
        {t("appointmentNotes.assignedStaff")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth sx={{ minWidth: 250 }}>
            <InputLabel id="coordinator-label">
              {t("appointmentNotes.coordinator")}
            </InputLabel>
            <Select
              multiple
              id="coordinator-select"
              labelId="coordinator-label"
              value={idsOf(selectedCoordinators)}
              onChange={(e) => {
                const value = e.target.value;
                const picked = mapIdsToEmployees(value, coordinatorOptions);
                setSelectedCoordinators(picked);
              }}
              label={t("appointmentNotes.coordinator")}
              renderValue={(selected) =>
                renderNames(selected, coordinatorOptions)
              }
            >
              <MenuItem value="">
                <em>{t("appointmentNotes.selectOption")}</em>
              </MenuItem>
              {coordinatorOptions.map((emp) => (
                <MenuItem key={`coord-${emp.id}`} value={String(emp.id)}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth sx={{ minWidth: 250 }}>
            <InputLabel id="doctor-label">
              {t("appointmentNotes.doctor")}
            </InputLabel>
            <Select
              multiple
              id="doctor-select"
              labelId="doctor-label"
              value={idsOf(selectedDoctors)}
              onChange={(e) => {
                const value = e.target.value;
                const picked = mapIdsToEmployees(value, doctorOptions);
                setSelectedDoctors(picked);
              }}
              label={t("appointmentNotes.doctor")}
              renderValue={(selected) => renderNames(selected, doctorOptions)}
            >
              <MenuItem value="">
                <em>{t("appointmentNotes.selectOption")}</em>
              </MenuItem>
              {doctorOptions.map((emp) => (
                <MenuItem key={`doc-${emp.id}`} value={String(emp.id)}>
                  {emp.first_name} {emp.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        {t("appointmentNotes.noteForFloorManagerTitle")}
      </Typography>
      <TextField
        label={t("appointmentNotes.noteForFloorManagerLabel")}
        placeholder={t("appointmentNotes.notePlaceholder")}
        variant="outlined"
        multiline
        rows={3}
        fullWidth
        sx={{ mt: 1 }}
        value={floorManagerNotes}
        onChange={(e) => setFloorManagerNotes(e.target.value)}
      />

      <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
        {t("appointmentNotes.noteForNextAppointmentTitle")}
      </Typography>

      {sourceNurseNoteRef.current && (
        <Box sx={{ mb: 2 }}>
          <FormControl
            required
            error={formSubmitted && carryOverNextAppointmentNote === null}
          >
            <FormLabel id="confirm-note-validity">
              {t(
                "appointmentNotes.confirmNoteValidity",
                "Použít poznámku z minula",
              )}
            </FormLabel>
            <RadioGroup
              aria-labelledby="confirm-note-validity"
              style={{ display: "flex", flexDirection: "row", gap: "2rem" }}
              onChange={(e) => handleRadioChange(e.target.value === "true")}
            >
              <FormControlLabel value={true} control={<Radio />} label="Ano" />
              <FormControlLabel value={false} control={<Radio />} label="Ne" />
            </RadioGroup>
            {formSubmitted && carryOverNextAppointmentNote === null && (
              <FormHelperText>Výběrem potvrďte jednu z možností</FormHelperText>
            )}
          </FormControl>
        </Box>
      )}

      <TextField
        label={t("appointmentNotes.noteForNextAppointmentLabel")}
        placeholder={t("appointmentNotes.notePlaceholder")}
        variant="outlined"
        multiline
        rows={3}
        fullWidth
        sx={{ mt: 1 }}
        value={nextAppointmentNote}
        onChange={handleTextChange}
        disabled={carryOverNextAppointmentNote}
      />
    </Box>
  );
}
